import $ from 'jquery';

import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';

import { isMobilePage } from 'lib/util/helpers';

interface MessageHandlers {
	[key : string] : (mod : IFrame, options : any, msg : MessageEvent, msgData : any) => void;
}

const MESSAGE_HANDLERS : MessageHandlers = {
	/**
	 * Open dialog
	 * @param _mod
	 * @param dialogOptions
	 */
	dialog(_mod, dialogOptions) {
		dialogOptions.classes = $.extend({}, {'ui-dialog': dialogOptions.dialogClass || 'wide'}, dialogOptions.classes || {});
		dialogOptions.width = dialogOptions.width || (800 - 30);
		const content = dialogOptions.html;

		delete dialogOptions.html;
		delete dialogOptions.dialogClass;

		$('<div/>').html(content).dialog(dialogOptions);
	},
	/**
	 * Scroll to top
	 *
	 * @param mod
	 * @param top
	 */
	top(mod, top) {
		if (top.startsWith('iframe')) {
			top = parseInt(top.split('|')[1], 10) || 0;
			top += mod.$iframe.offset()!.top;
		} else {
			top = parseInt(top, 10) || 0;
		}
		window.scrollTo(0, top);
	},
	/**
	 * Resize iframe height
	 * (only in non-adaptive mode)
	 *
	 * @param mod
	 * @param height
	 */
	height(mod, height) {
		if(mod.adaptive) {
			return;
		}
		mod.$iframe.attr('height', parseInt(height, 10));
	},
	/**
	 * Used for iframes that do not use tracking code but need to track things, so they can post a message
	 *
	 * @param mod
	 * @param trackingData
	 */
	trackTealium(mod, trackingData) {
		mod.log('trackTealium called with trackingData', trackingData);
		new SCS.portal.datalayer.Interaction()
			.withEvent(
				trackingData.action,
				trackingData.label,
				trackingData.location,
				trackingData.category,
				trackingData.friendlyName,
			)
			.withConversion(
				trackingData.conversionCategory,
				trackingData.conversionDetail,
				trackingData.conversionClass,
			)
			.track();
	},
};
// Alias scrollTop to top for www.digitalassessment.ch iframes
MESSAGE_HANDLERS.scrollTop = MESSAGE_HANDLERS.top;

function isVideo(url : string) {
	return url.indexOf('youtube.com') > -1;
}

// List of allowed domains. Matched against scheme, host and port.
// Be careful when using regular expressions here to not match too little (always anchor at the end!)
const ALLOWED_MESSAGE_SOURCES = [
	/[./](swisscom|bwns)\.ch?$/,
	/\/{2}(www\.)?bluewin\.ch?$/,
	/\/{2}(www\.)?swisscom-learningcenter\.ch$/,
	'https://www.digitalassessment.ch',
	// The current host is always allowed.
	`${window.location.protocol}//${window.location.host}`,
];

function originAllowed(origin : string, additionalAllowedSources? : (string | RegExp)[]) {
	if(!origin) {
		return false;
	}
	let checks = ALLOWED_MESSAGE_SOURCES;
	if(additionalAllowedSources) {
		checks = checks.concat(additionalAllowedSources);
	}
	for(const check of checks) {
		if(check instanceof RegExp) {
			if(check.test(origin)) {
				return true;
			}
		} else if(check === origin) {
			return true;
		}
	}

	return false;
}

export default class IFrame extends Module {
	public adaptive! : boolean;
	public $iframe! : JQuery<HTMLIFrameElement>;

	private aspectRatio! : number;
	private allowfullscreen! : boolean;
	private allowedMessageSources! : string[];
	private considerHashParameters! : boolean;
	private disallowMessages! : string;
	private height! : number;
	private scrollbar! : boolean;
	private url! : string;
	private width! : number;
	/**
	 * Hook function to do all of your module stuff.
	 *
	 * @method on
	 * @param  {Hook} callback Callback function
	 * @return void
	 */
	public on(callback : Hook) {
		this.readProperties();

		this.$iframe = this.$ctx.find('iframe') as JQuery<HTMLIFrameElement>;
		if(!this.$iframe.length) {
			// Initialize the iframe only if none exists already
			this.initializeIframe();
			this.$iframe = this.$ctx.find('iframe') as JQuery<HTMLIFrameElement>;
		}

		this.addMessageListeners();

		callback();
	}

	/**
	 * Initializing properties read from data properties
	 * on this context's div tag.
	 */
	private readProperties() : void {
		this.url = this.$ctx.data('src');
		this.width = this.$ctx.data('width');
		this.height = this.$ctx.data('height');

		this.disallowMessages = this.$ctx.data('disallow-messages');
		this.considerHashParameters = this.$ctx.data('considerhashparameters');
		this.scrollbar = this.$ctx.data('scrollbar');

		this.adaptive = this.$ctx.data('adaptive');
		if(isMobilePage() && isVideo(this.url)) {
			this.adaptive = true;
		}

		let allowedMessageSources = (this.$ctx.attr('data-allowed-message-sources') || '').trim().split(/\s*,\s*/);
		if(allowedMessageSources.length === 1 && !allowedMessageSources[0]) {
			allowedMessageSources = [];
		}
		this.allowedMessageSources = allowedMessageSources;

		this.aspectRatio = parseFloat(this.$ctx.attr('data-aspect-ratio') || '');
		if(isNaN(this.aspectRatio)) {
			this.aspectRatio = 16 / 9;
		}
		this.allowfullscreen = this.$ctx.data('allowfullscreen');
	}

	/**
	 * Display Loading Rotator until document ready, then insert iframe
	 *
	 * @method initializeIframe
	 * @return void
	 */
	private initializeIframe() : void {
		if (this.considerHashParameters) {
			const locationHash = window.location.hash.substr(1);
			const locationParams = new URLSearchParams(locationHash);
			const url = new URL(this.url, window.location.href);
			locationParams.forEach((value, key) => {
				url.searchParams.append(key, value);
			});

			this.url = url.href;
		}

		const iframe = $('<iframe/>').attr({
			frameborder: 0,
			height: this.height,
			scrolling: this.scrollbar,
			src: this.url,
			width: this.width,
		});

		if (this.allowfullscreen) {
			iframe.attr({
				allowfullscreen: '',
			});
		}

		try {
			this.$ctx.empty().append(iframe);
		} catch(e) {
			// IE throws an error because of iFrame security but it works anyway
		}

		if(this.adaptive) {
			iframe.wrap($('<div/>').addClass('fixedAspectRatio').css({paddingBottom: `${(100 / this.aspectRatio)}%`}));
		}
	}

	/**
	 * Attaching event listener to the window, so the
	 * iframe containing content can send messages to this module
	 */
	private addMessageListeners() : void {
		if(this.disallowMessages) {
			return;
		}

		window.addEventListener('message', this.listenMessage.bind(this));
	}

	/**
	 * Handle messages sent from iFrame using postMessage
	 */
	private listenMessage(msg : MessageEvent) : void {
		if(this.disallowMessages) {
			return;
		}
		// Ignore non-JSON messages
		if(!msg || !msg.data || !msg.data.match || !msg.data.match(/^\{/)) {
			return;
		}
		// Ignore messages from other iframes
		if(!msg.source || this.$iframe[0].contentWindow !== msg.source) {
			return;
		}

		// Check the origin
		if(!originAllowed(msg.origin, this.allowedMessageSources)) {
			this.log.error('Got message from unknown origin', msg.origin, msg);

			return;
		}

		const msgData = JSON.parse(msg.data);
		for(const type in MESSAGE_HANDLERS) {
			if(type in msgData) {
				this.log(`Got ${type} message`, msgData[type]);
				MESSAGE_HANDLERS[type](this, msgData[type], msg, msgData);
			}
		}
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/containers/Tc.Module.IFrame.ts